import axios from 'axios';
import logger from '@/utils/logger';
import stringify from '@/utils/stringify';
import env from '@/utils/env';
import store from '@/store';

/**
 * 服务基类
 */
export default class Service {
    /**
     * 构造函数
     */
    constructor() {
        // 是否调用mock接口
        this.isMock = false;
        this.mockPrefix = 'mockPrefix';
        this.serverPrefix = 'serverPrefix';

        // 初始化axios实例
        this.axios = axios.create({
            // Url = base url + request url
            baseURL: '/',
            headers: {
                'Content-Type': 'application/json'
            },
            paramsSerializer: params => stringify(params),
            validateStatus: status => status >= 200 && status < 300
        });

        // 安装拦截器
        this.setupRequestInterceptors();
        this.setupResponseInterceptors();
    }
    /**
     * 安装请求拦截器
     */
    setupRequestInterceptors() {
        // 请求拦截器
        this.axios.interceptors.request.use(
            config => {
                if (store.state.user.token) {
                    // 给每次请求带上token
                    config.headers.Authorization = `Bearer ${store.state.user.token}`;
                }

                if (env.VUE_APP_LOCAL) {
                    // 反向代理前缀
                    const proxyPrefix = this.isMock ? this.mockPrefix : this.serverPrefix;

                    // 添加反向代理前缀
                    config.url = `${proxyPrefix}/${config.url}`;
                }
                // 如果接口前缀带api/pay/ 则替换为环境变量VUE_APP_NG_SERVER_URL
                config.url = config.url.replace('api/pay/', process.env.VUE_APP_NG_SERVER_URL);

                // 给请求添加时间戳
                config.params = config.params || {};
                config.params.request_no = new Date().getTime();
                return config;
            },
            error => {
                logger.error(error);
                return Promise.reject(error);
            }
        );
    }
    /**
     * 安装响应拦截器
     */
    setupResponseInterceptors() {
        // 响应拦截器
        this.axios.interceptors.response.use(
            response => {
                const defaultError = {
                    message: '业务错误',
                    code: 'BUSINESS_ERROR'
                };

                if (!response) {
                    defaultError.code = '没有响应内容';
                    return Promise.reject(defaultError);
                }
                if (!response.data) {
                    defaultError.code = '未返回data字段';
                    return Promise.reject(defaultError);
                }
                if (response.config.url.startsWith('api/pay-dms/') || response.config.url.startsWith('api/pay/')) {
                    if (!response.data.returnCode || response.data.returnCode !== 'SUCCESS') {
                        defaultError.code = response.data.returnCode;
                        defaultError.message = response.data.returnMsg;
                        return Promise.reject(defaultError);
                    }
                    if (!response.data.resultCode || response.data.resultCode !== 'SUCCESS') {
                        defaultError.code = response.data.resultCode;
                        defaultError.message = response.data.errorMsg;
                        return Promise.reject(defaultError);
                    }
                }
                return response;
            },
            error => {
                const defaultError = error.response || {
                    message: '网络超时',
                    code: 500
                };
                // 身份验证失败
                let isValidateFail = false;

                // 身份验证失败后是否需要跳转
                let redirectWhenFail = false;

                if (error.response && error.response.data) {
                    defaultError.code = error.response.data.code;
                    defaultError.message = error.response.data.message;

                    switch (parseInt(error.response.data.code || 200, 10)) {
                        case 40001:
                            defaultError.message = '该账号已在其它地方登录';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40002:
                            defaultError.message = '没有权限访问，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40003:
                            // 优先判断是否被踢出
                            defaultError.message = '用户被停用，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40004:
                            // 优先判断是否被踢出
                            defaultError.message = '登录过期，请重新登录';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40006:
                            // 优先判断是否被踢出
                            defaultError.message = '账号认证失败，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 44129:
                            defaultError.message = '当前登录已失效';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 101012:
                            // 优先判断是否被踢出
                            defaultError.message = '无权访问，请联系管理员';
                            isValidateFail = false;
                            redirectWhenFail = false;
                            break;
                        case 101021:
                            // 优先判断是否被踢出
                            defaultError.message = '用户名或密码错误';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 101022:
                            // 优先判断是否被踢出
                            defaultError.message = '当前登录已失效，请重新登录';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        default:
                            break;
                    }

                    if (isValidateFail && redirectWhenFail) {
                        // 身份存在问题并且需要跳转到登录页面
                        window.location.href = `${env.VUE_APP_LOGIN_URL}/#/login`;
                    }
                }

                return Promise.reject(defaultError);
            }
        );
    }
    /**
     * 导入
     * @param file
     * @param path
     */
    importTemplate(file, path) {
        const formData = new FormData();
        formData.append('file', file);
        return this.postFormData(formData, path);
    }
    /**
     * 提交formData
     */
    postFormData(formData, path) {
        return this.axios
            .post(path, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(res => {
                if (res) {
                    return Promise.resolve(res);
                }
            })
            .catch(err => {
                return Promise.reject(err);
            });
    }
}
